const IOS_USER_AGENT_PATTERN = /iPad|iPhone|iPod/i;

export type PlainTextCopyEnvironment = {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  copyUsingSelection: (text: string) => boolean;
  writeText: (text: string) => Promise<void>;
};

const isIOS = ({
  userAgent,
  platform,
  maxTouchPoints,
}: Pick<
  PlainTextCopyEnvironment,
  "userAgent" | "platform" | "maxTouchPoints"
>) =>
  IOS_USER_AGENT_PATTERN.test(userAgent) ||
  // iPadの「デスクトップ用サイト表示」(既定ON)ではUAとplatformがMacを名乗るため、
  // タッチ点数で判別する
  (platform === "MacIntel" && maxTouchPoints > 1);

const copyUsingSelection = (text: string): boolean => {
  if (!document.body || typeof document.execCommand !== "function") {
    return false;
  }

  const activeElement = document.activeElement;
  const selection = document.getSelection();
  const selectedRanges = selection
    ? Array.from({ length: selection.rangeCount }, (_, index) =>
        selection.getRangeAt(index).cloneRange(),
      )
    : [];
  // Range は選択方向を持たないため、逆向き選択(focus が anchor より前)も
  // 復元できるよう端点を別途控える
  const anchorNode = selection?.anchorNode ?? null;
  const anchorOffset = selection?.anchorOffset ?? 0;
  const focusNode = selection?.focusNode ?? null;
  const focusOffset = selection?.focusOffset ?? 0;
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.readOnly = true;
  textArea.tabIndex = -1;
  // フォーカスを当てる要素に aria-hidden を付けるのは WAI-ARIA 違反のため付けない
  textArea.style.cssText =
    "position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;outline:0;box-shadow:none;background:transparent;opacity:0;pointer-events:none;font-size:12pt;";
  document.body.appendChild(textArea);

  try {
    textArea.focus({ preventScroll: true });
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textArea.remove();
    const HTMLElementConstructor = document.defaultView?.HTMLElement;
    if (
      HTMLElementConstructor &&
      activeElement instanceof HTMLElementConstructor
    ) {
      try {
        activeElement.focus({ preventScroll: true });
      } catch {
        // コピー前の要素が処理中に破棄された場合は、選択範囲だけ復元する。
      }
    }
    if (selection) {
      selection.removeAllRanges();
      if (anchorNode && focusNode) {
        try {
          selection.setBaseAndExtent(
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset,
          );
        } catch {
          selectedRanges.forEach((range) => selection.addRange(range));
        }
      } else {
        selectedRanges.forEach((range) => selection.addRange(range));
      }
    }
  }
};

export const createBrowserEnvironment = (): PlainTextCopyEnvironment | null => {
  if (typeof navigator === "undefined" || typeof document === "undefined") {
    return null;
  }

  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    copyUsingSelection,
    writeText: async (text: string) => {
      if (!navigator.clipboard) {
        throw new Error("Clipboard API is not available");
      }
      await navigator.clipboard.writeText(text);
    },
  };
};

// この環境では copyPlainText が選択コピー(execCommand)方式で動作する。選択コピーは
// ユーザー操作の同期的な延長でしか成功しないため、要約完成時の自動コピーのような
// ユーザー操作外の書き込みはこの環境では試行しないこと(SUCCESS-99)。
export const isSelectionCopyEnvironment = (
  environment: PlainTextCopyEnvironment | null = createBrowserEnvironment(),
): boolean => environment !== null && isIOS(environment);

export const copyPlainText = async (
  text: string,
  environment: PlainTextCopyEnvironment | null = createBrowserEnvironment(),
): Promise<boolean> => {
  if (!environment) return false;

  // コピーする文字列が「S:」などURIスキーム形式(英字+コロン)で始まると、iOSの
  // WebKitはwriteText時に全文をpercent-encodeしたURL表現(public.url)をpasteboardへ
  // 追加する(iPad/iPhoneとも実測確認済み)。URL表現を優先して読む貼り付け先
  // (ウィーメックス メディコム等の電子カルテ)では全文が「%E3..」形式に化ける。
  // SOAP形式の要約は「S:」始まりのため常に該当する。選択コピー(execCommand)は
  // URL表現を付けないので正常に貼れる。失敗時にwriteTextへフォールバックすると
  // 化ける構成が復活するため、意図的に戻さない。
  if (isIOS(environment)) {
    return environment.copyUsingSelection(text);
  }

  try {
    await environment.writeText(text);
    return true;
  } catch {
    return false;
  }
};
